import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  makeStyles,
  Text,
  Button,
  Badge,
  Spinner,
  Textarea,
  Dropdown,
  Option,
} from '@fluentui/react-components';
import { ArrowLeftRegular, SaveRegular, WarningRegular } from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { PermissionGate, ProtectedButton } from '../../../components/common';
import {
  ApiClient,
  getErrorMessage,
  newToast,
  fullDate,
  API_URL,
} from '../../../configurations/index.config';
import type { IncidentReport, User } from '../../../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#323130',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  detailLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#605e5c',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  detailValue: {
    fontSize: '14px',
    color: '#323130',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    paddingTop: '16px',
    borderTop: '1px solid #edebe9',
  },
  fullWidth: {
    gridColumn: '1 / -1',
  },
});

const IncidentDetailsFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [incident, setIncident] = useState<IncidentReport | null>(null);
  const [users, setUsers] = useState<User[]>([]);

  const [editMode, setEditMode] = useState(false);
  const [status, setStatus] = useState<string>('');
  const [severity, setSeverity] = useState<string>('');
  const [assignedTo, setAssignedTo] = useState<number | undefined>(undefined);
  const [actionsTaken, setActionsTaken] = useState('');

  useEffect(() => {
    if (id) {
      fetchIncident();
      fetchUsers();
    }
  }, [id]);

  const fetchIncident = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.get(`/get-incident/${id}`);
      if (response.data.status === 'success') {
        const data = response.data.data;
        setIncident(data);
        setStatus(data.status);
        setSeverity(data.severity);
        setAssignedTo(data.assigned_to);
        setActionsTaken(data.actions_taken || '');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
      navigate('/account/incident-reports');
    }
    setLoading(false);
  };

  const fetchUsers = async () => {
    try {
      const response = await ApiClient.get('/get-user-data');
      if (response.data.status === 'success') {
        setUsers(response.data.data || []);
      }
    } catch (error: unknown) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleSave = async () => {
    if (!incident) {
      return;
    }

    setSaving(true);
    try {
      const response = await ApiClient.post('/update-incident-report', {
        id: incident.id,
        status,
        severity,
        assigned_to: assignedTo,
        actions_taken: actionsTaken,
      });

      if (response.data.status === 'success') {
        newToast('success', 'Incident updated successfully');
        setEditMode(false);
        fetchIncident();
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setSaving(false);
  };

  const getSeverityBadge = (sev: string) => {
    const colors: Record<string, any> = {
      low: { color: 'informative', label: 'Low' },
      medium: { color: 'warning', label: 'Medium' },
      high: { color: 'important', label: 'High' },
      critical: { color: 'danger', label: 'Critical' },
    };
    const config = colors[sev] || { color: 'subtle', label: sev };
    return (
      <Badge appearance="filled" color={config.color}>
        {config.label}
      </Badge>
    );
  };

  const getStatusBadge = (stat: string) => {
    const colors: Record<string, any> = {
      open: { color: 'danger', label: 'Open' },
      in_progress: { color: 'warning', label: 'In Progress' },
      resolved: { color: 'success', label: 'Resolved' },
      closed: { color: 'subtle', label: 'Closed' },
    };
    const config = colors[stat] || { color: 'subtle', label: stat };
    return (
      <Badge appearance="tint" color={config.color}>
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <Spinner label="Loading incident details..." />
      </div>
    );
  }

  if (!incident) {
    return null;
  }

  return (
    <PermissionGate privilege="incidents.view" showDenied>
      <div className={styles.container}>
        <div className={styles.pageHeader}>
          <div className={styles.headerLeft}>
            <Button
              appearance="subtle"
              icon={<ArrowLeftRegular />}
              onClick={() => navigate('/account/incident-reports')}
            />
            <div>
              <Text className={styles.pageTitle}>{incident.incident_type}</Text>
            </div>
          </div>

          <div>
            {!editMode ? (
              <ProtectedButton
                privilege="incidents.update"
                appearance="primary"
                onClick={() => setEditMode(true)}
              >
                Edit
              </ProtectedButton>
            ) : null}
          </div>
        </div>

        <ContentCard>
          <Text size={500} weight="semibold" style={{ display: 'block', marginBottom: '24px' }}>
            Incident Information
          </Text>

          <div className={styles.detailsGrid}>
            <div className={styles.detailItem}>
              <Text className={styles.detailLabel}>Site</Text>
              <Text className={styles.detailValue}>{incident.site_name || '—'}</Text>
            </div>

            <div className={styles.detailItem}>
              <Text className={styles.detailLabel}>Reported By</Text>
              <Text className={styles.detailValue}>{incident.employee_name || '—'}</Text>
            </div>

            <div className={styles.detailItem}>
              <Text className={styles.detailLabel}>Incident Date</Text>
              <Text className={styles.detailValue}>{fullDate(incident.incident_timestamp)}</Text>
            </div>

            <div className={styles.detailItem}>
              <Text className={styles.detailLabel}>Incident Type</Text>
              <Text className={styles.detailValue}>{incident.incident_type}</Text>
            </div>

            <div className={styles.detailItem}>
              <Text className={styles.detailLabel}>Severity</Text>
              {editMode ? (
                <Dropdown
                  value={severity}
                  onOptionSelect={(_, data) => setSeverity(data.optionValue as string)}
                >
                  <Option value="low">Low</Option>
                  <Option value="medium">Medium</Option>
                  <Option value="high">High</Option>
                  <Option value="critical">Critical</Option>
                </Dropdown>
              ) : (
                getSeverityBadge(incident.severity)
              )}
            </div>

            <div className={styles.detailItem}>
              <Text className={styles.detailLabel}>Status</Text>
              {editMode ? (
                <Dropdown
                  value={status}
                  onOptionSelect={(_, data) => setStatus(data.optionValue as string)}
                >
                  <Option value="open">Open</Option>
                  <Option value="in_progress">In Progress</Option>
                  <Option value="resolved">Resolved</Option>
                  <Option value="closed">Closed</Option>
                </Dropdown>
              ) : (
                getStatusBadge(incident.status)
              )}
            </div>

            <div className={styles.detailItem}>
              <Text className={styles.detailLabel}>Assigned To</Text>
              {editMode ? (
                <Dropdown
                  placeholder="Select user"
                  value={
                    assignedTo ? users.find(u => u.userId === assignedTo)?.username : 'Unassigned'
                  }
                  onOptionSelect={(_, data) => {
                    setAssignedTo(
                      data.optionValue === 'none' ? undefined : Number(data.optionValue)
                    );
                  }}
                >
                  <Option value="none">Unassigned</Option>
                  {users.map(user => (
                    <Option key={user.userId} value={user.userId.toString()}>
                      {user.username}
                    </Option>
                  ))}
                </Dropdown>
              ) : (
                <Text className={styles.detailValue}>
                  {incident.assigned_to_name || 'Unassigned'}
                </Text>
              )}
            </div>

            {incident.resolved_at && (
              <div className={styles.detailItem}>
                <Text className={styles.detailLabel}>Resolved At</Text>
                <Text className={styles.detailValue}>{fullDate(incident.resolved_at)}</Text>
              </div>
            )}

            <div className={`${styles.detailItem} ${styles.fullWidth}`}>
              <Text className={styles.detailLabel}>Description</Text>
              <Text className={styles.detailValue}>{incident.description}</Text>
            </div>

            <div className={`${styles.detailItem} ${styles.fullWidth}`}>
              <Text className={styles.detailLabel}>Actions Taken</Text>
              {editMode ? (
                <Textarea
                  value={actionsTaken}
                  onChange={e => setActionsTaken(e.target.value)}
                  placeholder="Describe actions taken to address this incident"
                  rows={4}
                />
              ) : (
                <Text className={styles.detailValue}>
                  {incident.actions_taken || 'No actions recorded yet'}
                </Text>
              )}
            </div>

            {incident.gps_lat && incident.gps_lng && (
              <div className={styles.detailItem}>
                <Text className={styles.detailLabel}>GPS Location</Text>
                <Text className={styles.detailValue}>
                  {incident.gps_lat}, {incident.gps_lng}
                </Text>
              </div>
            )}

            {incident.photos && incident.photos.length > 0 && (
              <div className={`${styles.detailItem} ${styles.fullWidth}`}>
                <Text className={styles.detailLabel}>Photos</Text>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {incident.photos.map((photo, index) => (
                    <img
                      key={index}
                      src={`${API_URL}${photo}`}
                      alt={`Incident ${index + 1}`}
                      style={{
                        width: '150px',
                        height: '150px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        border: '1px solid #edebe9',
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {editMode && (
            <div className={styles.actions}>
              <Button
                appearance="secondary"
                onClick={() => {
                  setEditMode(false);
                  setStatus(incident.status);
                  setSeverity(incident.severity);
                  setAssignedTo(incident.assigned_to);
                  setActionsTaken(incident.actions_taken || '');
                }}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                icon={<SaveRegular />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </ContentCard>
      </div>
    </PermissionGate>
  );
};

export default IncidentDetailsFluentUI;
